import { useEffect, useState, useCallback } from 'react';
import { Modal } from '@nimbus-ds/components';
import {
  LocationDeleteByJobRequestDto,
  LocationResponseDto,
} from '@tiendanube/common';
import { useInventoryLevelsByLocation } from 'domains/Shipping/Locations/hooks';
import useLocationById from 'domains/Shipping/Locations/hooks/useLocation/useLocation';
import {
  ErrorOnLoadModal,
  DeleteLocationModalSkeleton,
  ConfirmDeleteWithoutInventoryModal,
  SelectTransferOrDeleteModal,
  ConfirmDeleteStockAndLocationModal,
  SelectDestinationLocationModal,
  ConfirmTransferStockAndDeleteLocationModal,
} from './components';

enum Steps {
  CONFIRM_DELETE_WITHOUT_INVENTORY = 'confirmDeleteWithoutInventory',
  SELECT_TRANSFER_OR_DELETE = 'selectTransferOrDelete',
  SELECT_DESTINATION = 'selectDestination',
  CONFIRM_DELETE = 'confirmDelete',
  CONFIRM_TRANSFER = 'confirmTransfer',
}

export type OperationType = LocationDeleteByJobRequestDto['operationType'];

export type DeleteLocationParams = {
  process: 'sync' | 'job';
  operationType: OperationType;
  locationId: string;
  locationDestinationId?: string;
};

interface DeleteLocationStepsModalProps {
  locationId: string;
  locationsList: LocationResponseDto[];
  onClose: () => void;
  onConfirm: (params: DeleteLocationParams) => void;
}

function DeleteLocationStepsModal({
  locationId,
  locationsList,
  onClose,
  onConfirm,
}: Readonly<DeleteLocationStepsModalProps>) {
  const [step, setStep] = useState<Steps | null>(null);
  const [selectedAction, setSelectedAction] = useState<OperationType | null>(
    null,
  );
  const [selectedDestinationId, setSelectedDestinationId] = useState<
    string | null
  >(null);

  const {
    location: { name: locationName },
  } = useLocationById(locationId);

  const { fetchInventoryLevels, isError, isLoading, isSuccess, total } =
    useInventoryLevelsByLocation(locationId);

  const handleSelectDeleteOrTransfer = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const action = e.target.value as OperationType;
      setSelectedAction(action);
    },
    [],
  );

  const handleConfirmDeleteOrTransfer = useCallback(() => {
    if (selectedAction === 'REMOVE') {
      setStep(Steps.CONFIRM_DELETE);
      return;
    }
    if (locationsList.length > 2) {
      setStep(Steps.SELECT_DESTINATION);
      return;
    }
    setStep(Steps.CONFIRM_TRANSFER);
  }, [locationsList.length, selectedAction]);

  const handleSelectDestination = useCallback((locationId: string) => {
    setSelectedDestinationId(locationId);
  }, []);

  useEffect(() => {
    fetchInventoryLevels();
  }, [fetchInventoryLevels]);

  useEffect(() => {
    if (!isSuccess) {
      return;
    }
    if (total === 0) {
      setStep(Steps.CONFIRM_DELETE_WITHOUT_INVENTORY);
    } else {
      setStep(Steps.SELECT_TRANSFER_OR_DELETE);
    }
  }, [isSuccess, total]);

  const otherDestination = locationsList.find((loc) => loc.id !== locationId);

  const views = {
    [Steps.CONFIRM_DELETE_WITHOUT_INVENTORY]: (
      <ConfirmDeleteWithoutInventoryModal
        locationName={locationName}
        onConfirm={() => {
          onConfirm({
            process: 'sync',
            operationType: 'REMOVE',
            locationId,
          });
        }}
        onClose={onClose}
      />
    ),
    [Steps.SELECT_TRANSFER_OR_DELETE]: (
      <SelectTransferOrDeleteModal
        locationName={locationName}
        isConfirmDisabled={selectedAction === null}
        onConfirm={handleConfirmDeleteOrTransfer}
        onSelectOption={handleSelectDeleteOrTransfer}
        onClose={onClose}
      />
    ),
    [Steps.CONFIRM_DELETE]: (
      <ConfirmDeleteStockAndLocationModal
        locationName={locationName}
        onConfirm={() =>
          onConfirm({
            process: 'job',
            operationType: 'REMOVE',
            locationId,
          })
        }
        onClose={onClose}
      />
    ),
    [Steps.SELECT_DESTINATION]: (
      <SelectDestinationLocationModal
        locationId={locationId}
        locationsList={locationsList}
        locationName={locationName}
        isConfirmDisabled={selectedDestinationId === null}
        onConfirm={() =>
          onConfirm({
            process: 'job',
            operationType: 'TRANSFER',
            locationId,
            locationDestinationId: selectedDestinationId ?? undefined,
          })
        }
        onSelectLocation={handleSelectDestination}
        onClose={onClose}
      />
    ),
    [Steps.CONFIRM_TRANSFER]: (
      <ConfirmTransferStockAndDeleteLocationModal
        locationName={locationName}
        destinationName={otherDestination?.name ?? ''}
        onConfirm={() =>
          onConfirm({
            process: 'job',
            operationType: 'TRANSFER',
            locationId,
            locationDestinationId: otherDestination?.id,
          })
        }
        onClose={onClose}
      />
    ),
  };

  const currentView = step ? views[step] : null;

  return (
    <Modal open onDismiss={onClose}>
      {isLoading && <DeleteLocationModalSkeleton />}
      {isError && (
        <ErrorOnLoadModal
          onRetryError={fetchInventoryLevels}
          onClose={onClose}
        />
      )}
      {isSuccess && currentView}
    </Modal>
  );
}

export default DeleteLocationStepsModal;
